import { useCallback, useRef, useState } from 'react';
import { UserRoles } from '@/enums/enums';
import { useStudentCustomFields } from '@/features/student/student-custom-field.queries';
import { useStudentCustomFieldDeleteMutation } from '@/features/student/student-custom-fields.mutations';
import { getUserOptions } from '@/features/user/user.queries';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Row } from '@tanstack/react-table';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { CustomFieldsDataTable } from '@/types/custom-field.types';
import { Button } from '@/components/ui/button';
import { DataTableRowActions } from '@/components/datatable/data-table-row-action';
import { TableDateColumn } from '@/components/datatable/data-table-simple/data-table-custom-columns';
import { DataTableSimple } from '@/components/datatable/data-table-simple/data-table-simple';
import AlertModal from '@/components/modals/alert-modal';

import StudentCustomFieldCreateUpdateModal from '../../../components/modals/student-custom-field-create-update-modal';

const studentCustomFieldRouteSchema = z.object({
  mode: z.enum(['create', 'update']).optional(),
  custom_field: z.number().optional(),
});

export const Route = createFileRoute('/_app/(students)/students/settings')({
  validateSearch: zodSearchValidator(studentCustomFieldRouteSchema),
  component: StudentSettingsPage,
  beforeLoad: async ({ context }) => {
    const { user } = await context.queryClient.ensureQueryData(getUserOptions);

    if (![UserRoles.Root, UserRoles.Admin].includes(user?.role as UserRoles)) {
      throw redirect({
        to: '/',
      });
    }
  },
});

function StudentSettingsPage() {
  return (
    <div className='flex h-full flex-col gap-y-2 px-4 py-2'>
      <SubjectsTabHeader />
      <StudentCustomFields />
    </div>
  );
}

function SubjectsTabHeader() {
  const navigate = useNavigate();
  const { t } = useTranslation(['student', 'common']);

  const onCreateNewCustomField = () => {
    navigate({
      to: '.',
      search: { mode: 'create' },
    });
  };

  return (
    <div className='flex flex-row justify-between py-4'>
      <div className='flex flex-col'>
        <span className='text-xl font-bold'>
          {t('custom_fields.page_title', { format: 'uppercase' })}
        </span>
        <span className='text-base font-light text-neutral-500'>
          {t('custom_fields.page_sub_title')}
        </span>
      </div>
      <div className='flex flex-row space-x-4'>
        <Button className='flex items-center gap-3' onClick={onCreateNewCustomField} size='default'>
          <Plus strokeWidth={4} className='h-6 w-6' />
          {t('student_custom_field', { ns: 'common' })}
        </Button>
      </div>
      <StudentCustomFieldCreateUpdateModal />
    </div>
  );
}

function StudentCustomFields() {
  const { t } = useTranslation(['table', 'common']);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const selectedDeleteId = useRef<number>(0);

  const navigate = useNavigate();
  const { data: customFields = [], isLoading } = useStudentCustomFields();

  const deleteMutation = useStudentCustomFieldDeleteMutation();

  const onEditDynamicProperty = (row: Row<CustomFieldsDataTable>) => {
    const { id } = row.original;
    navigate({
      to: '/students/settings',
      search: { mode: 'update', custom_field: id },
    });
  };

  const deleteDynamicProperty = (row: Row<CustomFieldsDataTable>) => {
    const { id } = row.original;
    selectedDeleteId.current = id;
    setDeleteOpen(true);
  };

  const onSuccessDelete = useCallback(async () => {
    await deleteMutation.mutateAsync(selectedDeleteId.current);
  }, [deleteMutation]);

  return (
    <>
      <DataTableSimple
        data={customFields}
        columns={['name', 'type', 'createdAt', 'updatedAt']}
        columnsLabels={{
          name: t('name'),
          type: t('type'),
          createdAt: t('created_at'),
          updatedAt: t('updated_at'),
        }}
        extraColumns={[
          {
            id: 'actions',
            size: 50,
            cell: ({ row }) => (
              <DataTableRowActions
                row={row}
                menuItems={[
                  {
                    label: t('edit', { ns: 'common' }),
                    onClick: onEditDynamicProperty,
                  },
                  {
                    label: t('delete', { ns: 'common' }),
                    onClick: deleteDynamicProperty,
                  },
                ]}
              />
            ),
          },
        ]}
        customRender={{
          type: (row) => {
            const value = row.getValue('type');
            return <div>{t(String(value), { ns: 'common' })}</div>;
          },
          createdAt: (row) => <TableDateColumn date={row.getValue('createdAt')} isISO />,
          updatedAt: (row) => <TableDateColumn date={row.getValue('updatedAt')} isISO />,
        }}
        isLoading={isLoading}
        className='flex h-full flex-col overflow-y-auto py-4'
      />
      <AlertModal
        title={'Are you absolutely sure?'}
        description={
          'This action cannot be undone. This will permanently delete the selected dynamic properties.'
        }
        setOpen={setDeleteOpen}
        isOpen={isDeleteOpen}
        onSuccess={onSuccessDelete}
      />
    </>
  );
}
